import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  Button,
  Grid,
  TextField,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Autocomplete,
  useTheme,
  Card,
  CardContent,
  Divider,
  Modal,
  IconButton
} from '@mui/material';
import { 
  School as SchoolIcon,
  Upload as UploadIcon,
  ContactMail as ContactIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { LocalizationProvider as RegularLocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs as RegularAdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { MuiTelInput } from 'mui-tel-input';
import { createAxiosInstance, API_URL } from '../../../api';
import axios from 'axios';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import successIcon from "../../../assets/success-icon.svg";
import errorIcon from "../../../assets/error-icon.svg";
import CourseBilling from '../../CourseBilling/CourseBilling';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import UploadCourseImage from '../../AddCourse/Components/UploadCourseImage';
import { getCourseImageDisplayUrlWithCleanup } from '../../../utils/imageUtils';

const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
const stripePromise = loadStripe(stripePublicKey);

const CreateCourseForm = ({ courseId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const userId = useSelector((state) => state.main.userId);
  const token = useSelector((state) => state.main.token);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({});
  const [isOngoing, setIsOngoing] = useState(false);
  const [isFree, setIsFree] = useState(false);
  const [showAddress, setShowAddress] = useState(false);
  const [editorData, setEditorData] = useState("");
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [createdCourse, setCreatedCourse] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imageDisplayData, setImageDisplayData] = useState({ url: '/assets/course-place1.png', cleanup: () => {} });
  const [isEditMode, setIsEditMode] = useState(false);
  const [courseLoading, setCourseLoading] = useState(false);

  const loadCourseData = async () => {
    try {
      setCourseLoading(true);
      console.log('Loading course data for courseId:', courseId);
      const response = await axios.get(`${process.env.REACT_APP_SERVER}/api/courses/${courseId}`);
      const courseData = response.data;
      console.log('Course data loaded:', courseData);
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      // Check if courseData is an array (some APIs return arrays)
      const actualCourseData = Array.isArray(courseData) ? courseData[0] : courseData;
      console.log('Actual course data:', actualCourseData);
      console.log('contactSources type:', typeof actualCourseData.contactSources);
      console.log('contactSources value:', actualCourseData.contactSources);
      console.log('contactSources isArray:', Array.isArray(actualCourseData.contactSources));
      
      // Update form state with course data (following old flow pattern)
      const { date, time, ...filteredCourseData } = actualCourseData;
      
      // Handle date conversion using dayjs (like old flow)
      const processedDate = date ? [dayjs(date[0]), dayjs(date[1])] : [];
      
      // Handle contactSources - check if it's string or array
      let contactSourcesArray = [];
      if (actualCourseData.contactSources) {
        if (typeof actualCourseData.contactSources === 'string') {
          // If it's a string, split by comma
          contactSourcesArray = actualCourseData.contactSources.split(',').map(s => s.trim());
        } else if (Array.isArray(actualCourseData.contactSources)) {
          // If it's an array, check if it contains strings or comma-separated strings
          if (actualCourseData.contactSources.length === 1 && actualCourseData.contactSources[0].includes(',')) {
            // Single element with comma-separated values
            contactSourcesArray = actualCourseData.contactSources[0].split(',').map(s => s.trim());
          } else {
            // Multiple elements or single element without commas
            contactSourcesArray = actualCourseData.contactSources;
          }
        }
      }
      
      // Map "message" to "our-message-system" to match old flow
      const mappedContactSources = contactSourcesArray.map(source => 
        source === 'message' ? 'our-message-system' : source
      );

      console.log('contactSourcesArray:', contactSourcesArray);
      console.log('mappedContactSources:', mappedContactSources);
      console.log('Email field should show:', mappedContactSources.includes('email'));
      console.log('Phone field should show:', mappedContactSources.includes('phone'));

      // Handle types - API returns array, ensure it's properly handled
      const typesArray = actualCourseData.types || [];

      console.log('About to call setCourse with:', {
        ...filteredCourseData,
        date: processedDate,
        time: time || '',
        types: typesArray,
        inPerson: typesArray.includes('in-person'),
        online: typesArray.includes('online'),
        contactSources: mappedContactSources,
      });

      // Handle time conversion - API returns string, convert to Date object for dayjs
      const processedTime = time ? new Date(`2000-01-01T${time}`) : null;

      setCourse(prevCourse => ({
        ...prevCourse,
        ...filteredCourseData,
        date: processedDate,
        time: processedTime,
        // Handle types array - API returns ['online'] or ['in-person'] or both
        types: typesArray,
        inPerson: typesArray.includes('in-person'),
        online: typesArray.includes('online'),
        // Handle contactSources - convert string to array and map values
        contactSources: mappedContactSources,
      }));

      console.log('setCourse call completed');

      // Handle free course logic (like old flow)
      if (actualCourseData.coursePrice === "Free" || !actualCourseData.coursePrice || actualCourseData.coursePrice === "0") {
        setIsFree(true);
      } else {
        setIsFree(false);
      }
      
      setIsOngoing(actualCourseData.isOngoing === 'true' || actualCourseData.isOngoing === true);
      setEditorData(actualCourseData.description || '');

      // Set separate state variables for dropdowns and form controls (like old flow)
      if (actualCourseData.subject) {
        setSelectedSubject(actualCourseData.subject);
        setSubject(actualCourseData.subject);
        console.log('Set selectedSubject and subject to:', actualCourseData.subject);
      }
      if (actualCourseData.level) {
        setSelectedLevel(actualCourseData.level);
        setLevel(actualCourseData.level);
        console.log('Set selectedLevel and level to:', actualCourseData.level);
      }

      console.log('Course state updated with:', {
        name: actualCourseData.name || '',
        subject: actualCourseData.subject || '',
        level: actualCourseData.level || '',
        coursePrice: actualCourseData.coursePrice || '',
        description: actualCourseData.description || '',
        types: actualCourseData.types || [],
        contactSources: actualCourseData.contactSources,
        mappedContactSources: mappedContactSources,
        isOngoing: actualCourseData.isOngoing,
        date: actualCourseData.date || [],
        telephone: actualCourseData.telephone || '',
        time: actualCourseData.time || '',
        processedTime: processedTime
      });

      // Handle image
      if (actualCourseData.image) {
        const imageUrl = getCourseImageDisplayUrlWithCleanup(actualCourseData.image);
        setImageDisplayData(imageUrl);
      }

    } catch (error) {
      console.error('Error loading course data:', error);
      setError({ general: 'Failed to load course data' });
    } finally {
      setCourseLoading(false);
    }
  };

  // Duration options matching the old form
  const durationOptions = [
    {
      key: "1",
      value: "£4.99",
    },
    {
      key: "2", 
      value: "£7.99",
    },
    {
      key: "6",
      value: "£12.99",
    },
    {
      key: "12",
      value: "£19.99",
    },
  ];
  
  // Subjects and levels
  const [subjects, setSubjects] = useState([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [subject, setSubject] = useState("");
  const [availableLevels, setAvailableLevels] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState("");
  const [level, setLevel] = useState("");
  
  // Course state
  const [course, setCourse] = useState({
    name: "",
    subject: "",
    level: "",
    date: [],
    time: "",
    duration: "Duration of Advertising",
    advPrice: "",
    addOne: "",
    addTwo: "",
    postCode: "",
    town: "",
    city: "",
    telephone: "",
    description: "",
    image: "",
    coursePrice: "",
    file: "",
    email: "",
    socialLink: "",
    by: "",
    types: [],
    contactSources: [],
    userId: userId,
    isOngoing: "",
  });

  // Debug course state changes
  useEffect(() => {
    console.log('Course state changed:', course);
  }, [course]);

  // Load course data if editing
  useEffect(() => {
    if (courseId) {
      console.log('Edit mode activated for courseId:', courseId);
      setIsEditMode(true);
      loadCourseData();
    }
  }, [courseId]);

  // Effect to set available levels when both subjects and course data are loaded
  useEffect(() => {
    if (subjects.length > 0 && selectedSubject) {
      const subjectObj = subjects.find(s => s.name === selectedSubject);
      if (subjectObj) {
        setAvailableLevels(subjectObj.levels || []);
      }
    }
  }, [subjects, selectedSubject]);

  // Sync level state with course state (like old flow)
  useEffect(() => {
    setCourse((prevCourse) => ({
      ...prevCourse,
      level: level,
    }));
  }, [level]);

  // Handle image display data cleanup
  useEffect(() => {
    return () => {
      imageDisplayData.cleanup();
    };
  }, [imageDisplayData]);

  useEffect(() => {
    if (course?.image) {
      const displayData = getCourseImageDisplayUrlWithCleanup(course.image);
      setImageDisplayData(displayData);
    } else {
      setImageDisplayData({ url: '/assets/course-place1.png', cleanup: () => {} });
    }
  }, [course?.image]);

  const debounceTimeoutRef = useRef(null);

  // Levels options
  const levels = [
    { value: "primary-7-11", key: "Primary (7/11+)" },
    { value: "primary-5-11", key: "Primary (5-11 yrs)" },
    { value: "secondary-11-14", key: "Secondary (11-14 yrs)" },
    { value: "gcse-15-16", key: "GCSE (15-16 yrs)" },
    { value: "a-level-5-18", key: "A-level (15-18 yrs)" },
    { value: "degree-adults", key: "Degree/Adults" },
    { value: "other", key: "Other" },
  ];

  // Advertising duration options
  const price = [
    { key: "1", value: "£4.99" },
    { key: "2", value: "£7.99" },
    { key: "6", value: "£12.99" },
    { key: "12", value: "£19.99" },
  ];

  // Fetch subjects on component mount
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_SERVER}/api/subjects`);
        setSubjects(response.data.subjects);
        setFilteredSubjects(response.data.subjects);
      } catch (error) {
        console.error("Error fetching subjects:", error);
      }
    };
    fetchSubjects();
  }, []);

  // Handle subject input change with debouncing
  const handleSubjectInputChange = async (value) => {
    if (value.length === 0) {
      setFilteredSubjects(subjects);
      setSelectedLevel("");
    } else if (value.length > 2) {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_SERVER}/api/subjects?search=${value}`
          );
          const subjectsData = response.data.subjects;
          setFilteredSubjects(subjectsData);
        } catch (error) {
          console.error("Error fetching filtered subjects:", error);
        }
      }, 300);
    }
  };

  // Handle checkbox changes
  const handleCheckboxChange = (event) => {
    const { value } = event.target;
    setCourse((prevCourse) => {
      const updatedTypes = prevCourse.types.includes(value)
        ? prevCourse.types.filter((type) => type !== value)
        : [...prevCourse.types, value];
      
      const shouldShowAddress = updatedTypes.includes("in-person");
      setShowAddress(shouldShowAddress);
      
      return { ...prevCourse, types: updatedTypes };
    });
  };

  const handleContactCheckboxChange = (event) => {
    const { value } = event.target;
    setCourse((prevCourse) => {
      const updatedContactSources = prevCourse.contactSources.includes(value)
        ? prevCourse.contactSources.filter((source) => source !== value)
        : [...prevCourse.contactSources, value];
      
      return { ...prevCourse, contactSources: updatedContactSources };
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    console.log('course', course);
    e.preventDefault();
    let newError = {};

    // Comprehensive validation
    if (course.name.trim().length === 0) newError.name = "Please input Course Name.";
    if ((!course?.date?.length || course.date.length === 0) && !isOngoing)
      newError.date = "Please input Course Date or Select OnGoing";
    if (!course.types || course.types.length === 0)
      newError.types = "Please select at least one course type (In-person or Online).";
    if (!course.image) newError.image = "Please upload an image.";
    if (course.advPrice.trim().length === 0) newError.advPrice = "Please Select a Package.";
    if (!isFree && (!course.coursePrice || course.coursePrice.trim().length === 0))
      newError.coursePrice = "Please enter a course price or mark as free.";
    if (course.types.includes("in-person") && course.addOne.length === 0)
      newError.address = "Please Enter Address";
    if (course.contactSources.includes("email") && course.email.trim().length === 0)
      newError.email = "Please Enter Email";
    if (course.contactSources.includes("phone") && course.telephone.trim().length === 0)
      newError.telephone = "Please Enter Telephone";
    if (course.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(course.email)) {
        newError.email = "Please enter a valid email address.";
      }
    }
    
    if (course.contactSources.length === 0)
      newError.contactSources = "Please select a contact source.";

    
    setError(newError);
    if (Object.keys(newError).length > 0) return;
    
    // Show payment modal first (before creating course)
    setIsPaymentModalOpen(true);
  };

  // Handle course creation/update after successful payment
  const createCourseAfterPayment = async () => {
    setLoading(true);
    try {
      // Prepare form data
      const formData = new FormData();
      
      // Handle file uploads - backend expects arrays
      if (course.image && typeof course.image !== "string") {
        formData.append("image", course.image);
      }
      if (course.file && typeof course.file !== "string") {
        formData.append("file", course.file);
      }

      // Prepare course data
      const courseData = {
        ...course,
        userId: userId,
        isOngoing: isOngoing ? "on-going" : "",
        coursePrice: isFree ? "0" : course.coursePrice,
        description: editorData || course.description,
        date: course.date ? course.date.map(d => d ? dayjs(d).format('YYYY-MM-DD') : null) : [],
        time: course.time ? dayjs(course.time).format('HH:mm') : "",
        types: course.types.join(','),
        contactSources: course.contactSources.join(','),
        // Remove file objects from course data
        image: undefined,
        file: undefined
      };
      formData.append("courseData", JSON.stringify(courseData));

      let response;
      if (isEditMode) {
        // Update existing course
        response = await createAxiosInstance().put(
          `${API_URL}/api/courses/edit/${courseId}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        // Create new course
        response = await createAxiosInstance().post(
          `${API_URL}/api/courses`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
      console.log('course 6', response);
      
      // Success handling
      const successTitle = isEditMode ? "Course Updated Successfully!" : "Thank you for your payment.";
      const successMessage = isEditMode 
        ? "Your course has been updated successfully."
        : "Your advert will appear once this has been approved by admin team (within 24 hours). You will also receive an email.";
      
      // Close the payment modal immediately after successful course creation
      setIsPaymentModalOpen(false);
      
      Swal.fire({
        imageUrl: successIcon,
        imageHeight: "100px",
        title: successTitle,
        html: successMessage,
        width: 600,
        confirmButtonColor: "#712d94",
        customClass: {
          icon: "no-before-icon",
        },
      }).then(() => {
        navigate("/my-courses");
      });

    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} course:`, error);
      
      // Close the payment modal on error too
      setIsPaymentModalOpen(false);
      
      // Error handling
      Swal.fire({
        imageUrl: errorIcon,
        imageHeight: "100px",
        title: "Oops...",
        text: error?.response?.data?.message || `Your course could not be ${isEditMode ? 'updated' : 'created'}, please try again!`,
        confirmButtonColor: "#712d94",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <Grid container spacing={3}>
        {/* Left Column - Basic Information */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SchoolIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Course Information
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Course Name */}
                <TextField
                  fullWidth
                  label="Name of course/class"
                  value={course.name}
                  onChange={(e) => setCourse(prev => ({ ...prev, name: e.target.value }))}
                  error={!!error.name}
                  helperText={error.name}
                />

                {/* Subject and Level */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Autocomplete
                    fullWidth
                    value={selectedSubject}
                    onInputChange={(_, value) => handleSubjectInputChange(value)}
                    onChange={(_, value) => {
                      setSelectedSubject(value || "");
                      setCourse((prev) => ({ ...prev, subject: value || "" }));
                      const selected = subjects.find((s) => s.name === value);
                      setAvailableLevels(selected?.levels || []);
                      setSelectedLevel("");
                    }}
                    options={filteredSubjects.map((s) => s.name)}
                    renderInput={(params) => (
                      <TextField {...params} label="Subject" />
                    )}
                  />
                  
                  <TextField
                    fullWidth
                    select
                    label="Level"
                    value={selectedLevel}
                    onChange={(e) => {
                      setSelectedLevel(e.target.value);
                      setCourse(prev => ({ ...prev, level: e.target.value }));
                    }}
                    disabled={availableLevels.length === 0}
                  >
                    {availableLevels.length === 0 ? (
                      <MenuItem value="Not needed">Not Needed</MenuItem>
                    ) : (
                      availableLevels.map((level) => (
                        <MenuItem key={level} value={level}>
                          {level}
                        </MenuItem>
                      ))
                    )}
                  </TextField>
                </Box>

                {/* Dates */}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateRangePicker
                    required
                    localeText={{
                      start: "Start Date",
                      end: isOngoing ? "On Going" : "End Date",
                    }}
                    label="Date Of Course"
                    value={course.date ? [course.date[0] ? dayjs(course.date[0]) : null, course.date[1] ? dayjs(course.date[1]) : null] : [null, null]}
                    onChange={(newValue) => {
                      const formattedDates = newValue ? [
                        newValue[0] ? newValue[0].toDate() : null,
                        newValue[1] ? newValue[1].toDate() : null
                      ] : [null, null];
                      setCourse(prev => ({ ...prev, date: formattedDates }));
                    }}
                    slotProps={{
                      textField: ({ position }) => ({
                        disabled: isOngoing && position === "end",
                        error: !!error.date,
                        helperText: error.date
                      }),
                    }}
                    disabled={isOngoing && course.date && course.date.length > 0 && course.date[0]}
                  />
                </LocalizationProvider>

                {/* On-going checkbox */}
                <FormControl>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isOngoing}
                          onChange={(e) => {
                            setIsOngoing(e.target.checked);
                            if (e.target.checked) {
                              setCourse(prev => ({ ...prev, isOngoing: "on-going" }));
                            } else {
                              setCourse(prev => ({ ...prev, isOngoing: "" }));
                            }
                          }}
                        />
                      }
                      label="On-going"
                    />
                  </FormGroup>
                </FormControl>

                {/* Time */}
                <RegularLocalizationProvider dateAdapter={RegularAdapterDayjs}>
                  <MobileTimePicker
                    label="Time of Course"
                    value={course.time ? dayjs(course.time) : null}
                    onChange={(time) => setCourse(prev => ({ ...prev, time: time ? time.toDate() : null }))}
                    ampm={false}
                    openTo="hours"
                    views={["hours", "minutes"]}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                  />
                </RegularLocalizationProvider>

                {/* Course Type */}
                <FormControl component="fieldset" error={!!error.types}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Course Type
                  </Typography>
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={course?.types?.includes("in-person")}
                          onChange={handleCheckboxChange}
                          value="in-person"
                        />
                      }
                      label="In-Person"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={course?.types?.includes("online")}
                          onChange={handleCheckboxChange}
                          value="online"
                        />
                      }
                      label="Online"
                    />
                  </FormGroup>
                  {error.types && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      {error.types}
                    </Typography>
                  )}
                </FormControl>

                {/* Address (if in-person) */}
                {showAddress && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      required
                      fullWidth
                      label="Address Line 1"
                      value={course.addOne}
                      onChange={(e) => setCourse(prev => ({ ...prev, addOne: e.target.value }))}
                      error={!!error.address}
                      helperText={error.address}
                    />
                    <TextField
                      fullWidth
                      label="Address Line 2"
                      value={course.addTwo}
                      onChange={(e) => setCourse(prev => ({ ...prev, addTwo: e.target.value }))}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Post Code"
                        value={course.postCode}
                        onChange={(e) => setCourse(prev => ({ ...prev, postCode: e.target.value }))}
                      />
                      <TextField
                        fullWidth
                        label="Town/City"
                        value={course.town}
                        onChange={(e) => setCourse(prev => ({ ...prev, town: e.target.value }))}
                      />
                    </Box>
                  </Box>
                )}

                {/* Course Price */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Course Price"
                      type="number"
                      value={course.coursePrice}
                      onChange={(e) => setCourse(prev => ({ ...prev, coursePrice: e.target.value }))}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>£</Typography>
                      }}
                      disabled={isFree}
                      error={!!error.coursePrice}
                      helperText={error.coursePrice}
                    />
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      minWidth: 'fit-content',
                      pt: 1.5 // Align with TextField
                    }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isFree}
                            onChange={(e) => {
                              setIsFree(e.target.checked);
                              if (e.target.checked) {
                                setCourse(prev => ({ ...prev, coursePrice: "0" }));
                              }
                            }}
                          />
                        }
                        label="Free"
                        sx={{ 
                          margin: 0,
                          '& .MuiFormControlLabel-label': {
                            fontSize: '0.875rem',
                            whiteSpace: 'nowrap'
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Advertising Duration */}
                <TextField
                  required
                  fullWidth
                  select
                  label="Duration of Advertising"
                  value={course.advPrice}
                  onChange={(e) => {
                    const selectedOption = durationOptions.find(opt => opt.value === e.target.value);
                    setCourse({ 
                      ...course, 
                      advPrice: selectedOption.value,
                      duration: selectedOption.key
                    });
                  }}
                  error={!!error.advPrice}
                  helperText={error.advPrice}
                >
                  {durationOptions.map((option) => (
                    <MenuItem key={option.key} value={option.value}>
                      {option.value}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Description */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Description
                  </Typography>
                  <Box sx={{ minHeight: 200 }}>
                    <CKEditor
                      editor={ClassicEditor}
                      data={editorData}
                      onChange={(event, editor) => {
                        const data = editor.getData();
                        setEditorData(data);
                        setCourse(prev => ({ ...prev, description: data }));
                      }}
                      config={{
                        toolbar: ['bold', 'italic', 'bulletedList', 'numberedList', 'link']
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Files & Contact */}
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* File Upload Card */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <UploadIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Files & Media
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Upload Course File
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Upload information about your course/class for users to download.
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Accept: PDF, Word Doc.
                    </Typography>
                    {course.file ? (
                      <Box sx={{ 
                        border: `1px solid ${theme.palette.primary.main}`,
                        borderRadius: 1,
                        p: 2,
                        mt: 1,
                        backgroundColor: theme.palette.primary.main + '05'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {typeof course.file === 'string' ? (
                              // Existing file from API (URL string)
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 500,
                                  color: 'primary.main',
                                  textDecoration: 'underline',
                                  cursor: 'pointer'
                                }}
                                onClick={() => window.open(course.file, '_blank')}
                              >
                                📄 View Course File
                              </Typography>
                            ) : (
                              // New file upload (File object)
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  📄 {course.file.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ({(course.file.size / 1024 / 1024).toFixed(2)} MB)
                                </Typography>
                              </>
                            )}
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => setCourse(prev => ({ ...prev, file: null }))}
                            sx={{ 
                              color: '#000',
                              '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.1)'
                              }
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    ) : (
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        sx={{ mt: 1 }}
                      >
                        Choose file
                        <input
                          type="file"
                          hidden
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setCourse(prev => ({ ...prev, file: file }));
                            }
                          }}
                        />
                      </Button>
                    )}
                  </Box>

                  <Divider />

                  <Box>
                    {/* Use the proper UploadCourseImage component */}
                    <UploadCourseImage
                      course={course}
                      setCourse={setCourse}
                      selectedImages={selectedImages}
                      setSelectedImages={setSelectedImages}
                      error={error}
                      imageDisplayData={imageDisplayData}
                      setImageDisplayData={setImageDisplayData}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Contact & Settings Card */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ContactIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Contact & Settings
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Website */}
                  <TextField
                    fullWidth
                    label="Website (optional)"
                    value={course.socialLink}
                    onChange={(e) => setCourse(prev => ({ ...prev, socialLink: e.target.value }))}
                  />

                  {/* Course by */}
                  <TextField
                    fullWidth
                    label="Course by (optional)"
                    value={course.by}
                    onChange={(e) => setCourse(prev => ({ ...prev, by: e.target.value }))}
                  />

                    {/* Contact Methods */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Contact Methods
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Select how potential clients can contact you. You must select at least one method.
                      </Typography>
                      
                      <FormControl component="fieldset" error={!!error.contactSources}>
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={course.contactSources.includes("email")}
                                onChange={handleContactCheckboxChange}
                                value="email"
                              />
                            }
                            label="Email"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={course.contactSources.includes("phone")}
                                onChange={handleContactCheckboxChange}
                                value="phone"
                              />
                            }
                            label="Phone"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={course.contactSources.includes("our-message-system")}
                                onChange={handleContactCheckboxChange}
                                value="our-message-system"
                              />
                            }
                            label="Our message system"
                          />
                        </FormGroup>
                        {error.contactSources && (
                          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                            {error.contactSources}
                          </Typography>
                        )}
                      </FormControl>

                    {/* Contact Details */}
                    {course.contactSources.includes("email") && (
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={course.email}
                        onChange={(e) => setCourse(prev => ({ ...prev, email: e.target.value }))}
                        error={!!error.email}
                        helperText={error.email}
                        sx={{ mt: 2 }}
                      />
                    )}

                    {course.contactSources.includes("phone") && (
                      <MuiTelInput
                        fullWidth
                        label="Phone Number"
                        value={course.telephone}
                        onChange={(value) => setCourse(prev => ({ ...prev, telephone: value }))}
                        error={!!error.telephone}
                        helperText={error.telephone}
                        sx={{ mt: 2 }}
                      />
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>

      {/* Submit Button */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mt: 3,
        pt: 2,
        borderTop: 1,
        borderColor: 'divider'
      }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={loading}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            borderRadius: 2,
            px: 6,
            py: 1.5,
            fontWeight: 700,
            fontSize: '1.1rem',
            textTransform: 'none',
            boxShadow: `0 4px 16px ${theme.palette.primary.main}30`,
            '&:active': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              boxShadow: `0 2px 8px ${theme.palette.primary.main}50`,
              transform: 'translateY(1px)'
            },
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              boxShadow: `0 6px 20px ${theme.palette.primary.main}40`,
              transform: 'translateY(-1px)'
            },
            '&:disabled': {
              background: `linear-gradient(135deg, ${theme.palette.grey[400]} 0%, ${theme.palette.grey[500]} 100%)`,
              color: theme.palette.grey[600],
              boxShadow: 'none',
              transform: 'none'
            }
          }}
        >
          {loading ? (isEditMode ? 'Updating Course...' : 'Creating Course...') : (isEditMode ? 'Update Course' : 'Submit Course')}
        </Button>
      </Box>

      {/* Payment Modal */}
      <Modal
        open={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        aria-labelledby="payment-modal-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000, // Higher than navbar and dashboard menu
          '&:focus': {
            outline: 'none'
          }
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 0,
            overflow: 'auto',
          }}
        >
          {/* Close Button */}
          <Button
            onClick={() => setIsPaymentModalOpen(false)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1,
              minWidth: 'auto',
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.1)',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.2)',
              }
            }}
          >
            ×
          </Button>

          {/* Payment Content */}
          <Elements stripe={stripePromise}>
            <CourseBilling
              advancePrice={course.advPrice}
              duration={course.duration}
              onPaymentSuccess={createCourseAfterPayment}
            />
          </Elements>
        </Box>
      </Modal>
    </Box>
  );
};

export default CreateCourseForm;
